import { HTTPClient } from "@abaco/web-common/src/HTTP";
import { PagedResults, PagingParams } from "@abaco/web-common/src/Paging";
import { AxiosError } from "axios";
import { plainToClass } from "class-transformer";
import { AccountURLs } from "../models/AccountURLs";
import { API_PATH } from "../models/Constants";
import { BulkPrintQueueData, ContractItem, PrintQueueData } from "../models/Contract";
import { Subject, SubjectResponse } from "../models/Subject";
import { DownloadFileParams } from "../models/ProjectProposal";
// import { sample } from "lodash"

export default class MaltaApi {
    private http: HTTPClient;
    private static instance: MaltaApi | null = null;

    private constructor(http: HTTPClient) {
        this.http = http;
    }

    public static getInstance(http: HTTPClient): MaltaApi {
        if (this.instance) {
            return this.instance;
        }
        this.instance = new MaltaApi(http);
        return this.instance;
    }

    async getAccountURLs(): Promise<AccountURLs> {
        return (await this.http.get<AccountURLs>(`${API_PATH}/portalconf`)).data;
    }

    async getSubjects(search?: string): Promise<SubjectResponse> {
        const data = (await this.http.get<SubjectResponse>(`${API_PATH}/subjects`, { params: { search } })).data;
        return plainToClass(SubjectResponse, data);
    }

    async getSubject(id: string): Promise<[Subject | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .get<Subject>(`${API_PATH}/subjects/${encodeURIComponent(id)}`)
                .then(resp => {
                    resolve([plainToClass(Subject, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err]);
                });
        });
    }

    async getContracts({ count, offset }: PagingParams, id: string): Promise<PagedResults<ContractItem>> {
        const data = (
            await this.http.get<PagedResults<ContractItem>>(`${API_PATH}/contracts/${encodeURIComponent(id)}`, {
                params: {
                    limit: count,
                    offset
                }
            })
        ).data;

        data.records = plainToClass(ContractItem, data.records);
        return data;
    }

    async getFile(
        subjectId: string | number,
        contractId: string | number,
        fileId: string | number
    ): Promise<[{ data: ArrayBuffer; filename: string; contentType: string } | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .get<ArrayBuffer>(`${API_PATH}/download/${subjectId}/${contractId}/${fileId}`, {
                    responseType: "arraybuffer"
                })
                .then(resp => {
                    const contentType = resp.headers["content-type"];
                    const content = resp.headers["content-disposition"] as string;
                    let filename = "";
                    content
                        ?.split(";")
                        .map((e: string) => e.trim())
                        .forEach(val => {
                            const [key, value] = val.replace(/"/gi, "").split("=");
                            if (key === "filename") {
                                filename = value;
                            }
                        });

                    resolve([
                        {
                            data: resp.data,
                            contentType,
                            //url: `data:${contentType};base64,${base64String}`,
                            filename
                        },
                        null
                    ]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async getPrintFile(
        subjectId: string | number,
        contractId: string | number,
        fileId: string | number
    ): Promise<[{ data: ArrayBuffer; filename: string; contentType: string } | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .get<ArrayBuffer>(`${API_PATH}/download/print/${subjectId}/${contractId}/${fileId}`, {
                    responseType: "arraybuffer"
                })
                .then(resp => {
                    const contentType = resp.headers["content-type"];
                    const content = resp.headers["content-disposition"] as string;
                    let filename = "";
                    content
                        ?.split(";")
                        .map((e: string) => e.trim())
                        .forEach(val => {
                            const [key, value] = val.replace(/"/gi, "").split("=");
                            if (key === "filename") {
                                filename = value;
                            }
                        });
                    resolve([
                        {
                            data: resp.data,
                            contentType,
                            //url: `data:${contentType};base64,${base64String}`,
                            filename
                        },
                        null
                    ]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async getPrintQueue(
        subjectId: string | number,
        contractId: string | number,
        applicationId: string | number,
        printId: string
    ): Promise<[PrintQueueData | null, AxiosError | null]> {
        try {
            const { data } = await this.http.get<PrintQueueData | null>(
                `${API_PATH}/print/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                    contractId
                )}/${encodeURIComponent(applicationId)}/${encodeURIComponent(printId)}`
            );
            return [data, null];
        } catch (err) {
            return [null, err as AxiosError];
        }
    }

    async performPrint(
        subjectId: string | number,
        contractId: string | number,
        applicationId: string | number,
        printId: string
    ): Promise<[PrintQueueData | null, AxiosError | null]> {
        try {
            const { data } = await this.http.post<PrintQueueData>(
                `${API_PATH}/print/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                    contractId
                )}/${encodeURIComponent(applicationId)}/${encodeURIComponent(printId)}`
            );
            return [data, null];
        } catch (err) {
            return [null, err as AxiosError];
        }
    }

    async getDownloadQueue(
        subjectId: string | number,
        applicationId: string | number,
        printId: string,
        printMsgKey: string
    ): Promise<[PrintQueueData | null, AxiosError | null]> {
        try {
            const { data } = await this.http.get<PrintQueueData | null>(
                `${API_PATH}/print/data/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                    applicationId
                )}/${encodeURIComponent(printId)}`,
                {
                    params: {
                        printMsgKey
                    }
                }
            );
            return [data, null];
        } catch (err) {
            return [null, err as AxiosError];
        }
    }

    async getDownloadFile(
        subjectId: string | number,
        applicationId: string | number,
        fileId: string | number,
        params: DownloadFileParams
    ): Promise<[{ data: ArrayBuffer; filename: string; contentType: string } | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .get<ArrayBuffer>(`${API_PATH}/print/data/download/${subjectId}/${applicationId}/${fileId}`, {
                    params,
                    responseType: "arraybuffer"
                })
                .then(resp => {
                    const contentType = resp.headers["content-type"];
                    const content = resp.headers["content-disposition"] as string;
                    let filename = "";
                    content
                        ?.split(";")
                        .map((e: string) => e.trim())
                        .forEach(val => {
                            const [key, value] = val.replace(/"/gi, "").split("=");
                            if (key === "filename") {
                                filename = value;
                            }
                        });
                    resolve([
                        {
                            data: resp.data,
                            contentType,
                            //url: `data:${contentType};base64,${base64String}`,
                            filename
                        },
                        null
                    ]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async performDownload(
        subjectId: string | number,
        applicationId: string | number,
        printId: string,
        printParameter: string,
        printMsgKey: string
    ): Promise<[PrintQueueData | null, AxiosError | null]> {
        try {
            const { data } = await this.http.post<PrintQueueData>(
                `${API_PATH}/print/data/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                    applicationId
                )}/${encodeURIComponent(printId)}`,
                {
                    printParameter,
                    printMsgKey
                }
            );
            return [data, null];
        } catch (err) {
            return [null, err as AxiosError];
        }
    }

    async getZipDownloadQueue(
        subjectId: string | number,
        applicationId: string | number,
        printId: string,
        bulkPrintQueueData: BulkPrintQueueData
    ): Promise<[BulkPrintQueueData | null, AxiosError | null]> {
        try {
            const { data } = await this.http.post<BulkPrintQueueData | null>(
                `${API_PATH}/print/data/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                    applicationId
                )}/bulk-queue-status/${encodeURIComponent(printId)}`,
                bulkPrintQueueData
            );
            return [data, null];
        } catch (err) {
            return [null, err as AxiosError];
        }
    }

    async performZipDownload(
        subjectId: string | number,
        applicationId: string | number,
        printId: string
    ): Promise<[BulkPrintQueueData | null, AxiosError | null]> {
        try {
            const { data } = await this.http.post<BulkPrintQueueData>(
                `${API_PATH}/print/data/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                    applicationId
                )}/bulk/${encodeURIComponent(printId)}`,
                {}
            );
            return [data, null];
        } catch (err) {
            return [null, err as AxiosError];
        }
    }

    async downloadAllCertificates(
        subjectId: string | number,
        applicationId: string | number,
        printId: string,
        bulkPrintQueueData: BulkPrintQueueData
    ): Promise<[{ data: ArrayBuffer; filename: string; contentType: string } | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .post<ArrayBuffer>(
                    `${API_PATH}/print/data/download/zip/${subjectId}/${applicationId}/${printId}`,
                    bulkPrintQueueData,
                    {
                        responseType: "arraybuffer"
                    }
                )
                .then(resp => {
                    const contentType = resp.headers["content-type"];
                    const content = resp.headers["content-disposition"] as string;
                    let filename = "";
                    content
                        ?.split(";")
                        .map((e: string) => e.trim())
                        .forEach(val => {
                            const [key, value] = val.replace(/"/gi, "").split("=");
                            if (key === "filename") {
                                filename = value;
                            }
                        });
                    resolve([
                        {
                            data: resp.data,
                            contentType,
                            filename
                        },
                        null
                    ]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }
}
